import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RootState, AppDispatch } from "../store/store";
import {
  fetchUserNotifications,
  setLastNotifiedCount,
} from "../store/NotificationSlice";
import {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  sendBrowserNotification,
} from "../utils/webNotification";

interface NotificationBellProps {
  variant?: "transparent" | "white";
}

export default function NotificationBell({ variant = "transparent" }: NotificationBellProps) {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
  const notificationState = useSelector((state: RootState) => state.notifications);
  const { totalPendingCount, items, lastNotifiedCount } = notificationState;

  // Initialize permission status
  useEffect(() => {
    if (isNotificationSupported()) {
      setPermission(getNotificationPermission());
    }
  }, []);

  // Fetch notifications on mount and periodically when logged in
  useEffect(() => {
    if (!isLoggedIn) return;

    dispatch(fetchUserNotifications());

    const interval = setInterval(() => {
      dispatch(fetchUserNotifications());
    }, 120000); // Check every 2 mins

    return () => clearInterval(interval);
  }, [dispatch, isLoggedIn]);

  // Trigger browser push notification when pending programs with questions are detected
  useEffect(() => {
    if (!isLoggedIn || permission !== "granted" || items.length === 0) {
      return;
    }

    const currentProgramIdsKey = items.map((i) => i.programId).sort().join(",");
    const lastNotifiedKey = sessionStorage.getItem("seva_last_notified_programs");

    // Only fire browser push notification if the set of programs with questions has changed
    if (lastNotifiedKey !== currentProgramIdsKey) {
      if (items.length === 1) {
        const firstItem = items[0];
        sendBrowserNotification("Questionnaire Ready 📋", {
          body: `You have a daily questionnaire ready to complete for ${firstItem.programTitle}.`,
          icon: "/images/Seva-logo.png",
          onClickUrl: firstItem?.bookingId
            ? `/booking/${firstItem.bookingId}`
            : "/bookings",
        });
      } else {
        sendBrowserNotification("Daily Questionnaires Ready 📋", {
          body: `You have questionnaires to complete for ${items.length} of your enrolled programs.`,
          icon: "/images/Seva-logo.png",
          onClickUrl: "/bookings",
        });
      }

      sessionStorage.setItem("seva_last_notified_programs", currentProgramIdsKey);
      dispatch(setLastNotifiedCount(items.length));
    }
  }, [items, permission, isLoggedIn, dispatch]);

  // Handle clicking outside to close popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleRequestPermission = async () => {
    const granted = await requestNotificationPermission();
    setPermission(granted ? "granted" : "denied");

    if (granted && items.length > 0) {
      sendBrowserNotification("Notifications Enabled! 🔔", {
        body: `You will now receive instant reminders when daily questionnaires are ready for your programs.`,
        icon: "/images/Seva-logo.png",
      });
    }
  };

  const isTransparent = variant === "transparent";

  if (!isLoggedIn) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
        className={`relative p-2.5 rounded-full transition-all duration-200 focus:outline-none flex items-center justify-center ${
          isTransparent
            ? "text-white/90 hover:text-white hover:bg-white/10"
            : "text-gray-600 hover:text-[#037897] hover:bg-gray-100"
        } ${isOpen ? (isTransparent ? "bg-white/15 text-white" : "bg-gray-100 text-[#037897]") : ""}`}
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Badge Indicator (Shows number of programs needing attention) */}
        {totalPendingCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-5 min-w-[20px] items-center justify-center px-1 rounded-full bg-[#B4804A] text-[11px] font-bold text-white shadow-xs animate-pulse ring-2 ring-white">
            {totalPendingCount > 99 ? "99+" : totalPendingCount}
          </span>
        )}
      </button>

      {/* Popover Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-3 w-80 sm:w-96 rounded-2xl bg-white shadow-2xl border border-gray-100 z-[9999] overflow-hidden animate-in fade-in duration-200">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-[#F9FAFC]">
            <div className="flex items-center gap-2">
              <h3 className="text-sm sm:text-base font-bold text-[#041B44]">
                {t("notifications.title", "Notifications")}
              </h3>
              {totalPendingCount > 0 && (
                <span className="bg-[#EBF7F9] text-[#037897] text-xs font-bold px-2.5 py-0.5 rounded-full border border-[#037897]/20">
                  {totalPendingCount} {t("notifications.pendingBadge", "Program(s) Pending")}
                </span>
              )}
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 text-sm font-bold"
            >
              ✕
            </button>
          </div>

          {/* Browser Permission Prompt Banner */}
          {isNotificationSupported() && permission === "default" && (
            <div className="p-3.5 bg-[#F0F9FB] border-b border-[#037897]/15 flex items-start gap-3">
              <span className="text-lg">🔔</span>
              <div className="flex-1 text-xs">
                <p className="font-bold text-[#041B44] mb-1">
                  {t("notifications.enableAlertsTitle", "Enable Desktop Alerts")}
                </p>
                <p className="text-gray-600 mb-2 leading-relaxed">
                  {t(
                    "notifications.enableAlertsDesc",
                    "Allow browser notifications to get instantly alerted when daily questionnaires or sessions are ready."
                  )}
                </p>
                <button
                  type="button"
                  onClick={handleRequestPermission}
                  className="bg-[#037897] hover:bg-[#025c74] text-white px-3 py-1.5 rounded-lg font-bold text-xs shadow-xs transition-colors"
                >
                  {t("notifications.allowButton", "Allow Notifications")}
                </button>
              </div>
            </div>
          )}

          {/* List of Notification Items */}
          <div className="max-h-80 overflow-y-auto custom-scrollbar divide-y divide-gray-100">
            {items && items.length > 0 ? (
              items.map((item) => (
                <div
                  key={item.programId}
                  className="p-4 hover:bg-[#F9FAFB] transition-colors flex items-start gap-3.5"
                >
                  {/* Program Icon / Avatar */}
                  <div className="w-11 h-11 rounded-xl bg-gray-100 border border-gray-200 overflow-hidden shrink-0">
                    <img
                      src={item.programIcon || "/images/innerb.jpg"}
                      alt={item.programTitle}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/images/innerb.jpg";
                      }}
                    />
                  </div>

                  {/* Info & Action */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-bold text-[#041B44] truncate mb-0.5">
                      {item.programTitle}
                    </h4>
                    <p className="text-xs text-[#037897] font-semibold mb-2 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-[#B4804A] shrink-0"></span>
                      <span>
                        {item.pendingCount}{" "}
                        {t("notifications.questionsPending", "question(s) pending")}
                      </span>
                    </p>

                    <Link
                      to={
                        item.bookingId
                          ? `/booking/${item.bookingId}`
                          : "/bookings"
                      }
                      onClick={() => setIsOpen(false)}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#037897] hover:bg-[#025c74] px-3.5 py-1.5 rounded-lg shadow-xs transition-colors"
                    >
                      <span>{t("notifications.answerNow", "Answer Now")}</span>
                      <span>&rarr;</span>
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              /* Empty State */
              <div className="py-10 px-6 text-center flex flex-col items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-[#EBF7F9] text-[#037897] flex items-center justify-center mb-3">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <p className="text-sm font-bold text-[#041B44] mb-1">
                  {t("notifications.allCaughtUp", "All caught up!")}
                </p>
                <p className="text-xs text-gray-500">
                  {t(
                    "notifications.noPending",
                    "You have answered all available questionnaires for today."
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Footer Link */}
          <div className="p-3 bg-gray-50 border-t border-gray-100 text-center">
            <Link
              to="/bookings"
              onClick={() => setIsOpen(false)}
              className="text-xs font-bold text-[#037897] hover:underline"
            >
              {t("notifications.viewAllBookings", "View All My Bookings & Timeline")} &rarr;
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
